/**
 * SSE-driven invalidation hook for the asset GeoJSON cache.
 *
 * Subscribes to `/api/admin/assets/state-changes` (Phase 4 Plan 05) and
 * invalidates `ASSETS_GEOJSON_KEY` on each `state_changed` event so the
 * PM map recolors within the NFR-PERF-3 budget (≤ 2 s p95) of a worker's
 * Save tap. Polling cannot meet that budget (5 s tick + ~350 ms server +
 * render = ~5.4 s p95 worst-case, per RESEARCH.md Q1 RESOLVED).
 *
 * Reconnection: native `EventSource` reconnects automatically on the
 * server's `Last-Event-ID` protocol when the connection drops. We add an
 * explicit close-and-reopen on `error` with exponential backoff
 * (1 s → 30 s ceiling) so a sustained outage doesn't busy-loop the
 * connect attempts. `withCredentials: true` ensures the `pm_session`
 * cookie is sent on the EventSource handshake (PM-only auth at the
 * server-side `Depends(require_pm)` gate).
 *
 * Cleanup: `useEffect` returns a cleanup that flips a `cancelled` flag
 * (so any pending `setTimeout` reconnect aborts) and closes the live
 * EventSource. The server-side `state_changes_pubsub.subscribe()`
 * `finally:` block then drains the subscriber set.
 *
 * CP4 compliance: events carry only `{asset_id: <int>}`. We log nothing
 * to the console besides connection-state transitions; the asset_id
 * itself is not credential-adjacent so this is safe.
 *
 * Returns `{connected, lastEventAt}` for an optional UI indicator
 * (e.g., a small status dot in MapView's corner).
 */
import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ASSETS_GEOJSON_KEY } from "../../api/assets";
import {
  ASSET_INSPECTIONS_KEY,
  INSPECTION_PHOTOS_KEY,
} from "../../api/inspections";

export interface AssetStateStreamStatus {
  connected: boolean;
  lastEventAt: Date | null;
}

const INITIAL_BACKOFF_MS = 1000;
const MAX_BACKOFF_MS = 30_000;

export function useAssetStateStream(): AssetStateStreamStatus {
  const qc = useQueryClient();
  const [connected, setConnected] = useState(false);
  const [lastEventAt, setLastEventAt] = useState<Date | null>(null);
  const esRef = useRef<EventSource | null>(null);

  useEffect(() => {
    let backoffMs = INITIAL_BACKOFF_MS;
    let cancelled = false;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;

    function connect() {
      if (cancelled) return;
      const es = new EventSource("/api/admin/assets/state-changes", {
        withCredentials: true,
      });
      esRef.current = es;

      es.onopen = () => {
        setConnected(true);
        backoffMs = INITIAL_BACKOFF_MS;
      };

      es.addEventListener("state_changed", (ev) => {
        try {
          const payload = JSON.parse((ev as MessageEvent).data);
          if (typeof payload.asset_id === "number") {
            setLastEventAt(new Date());
            qc.invalidateQueries({ queryKey: ASSETS_GEOJSON_KEY });
          }
        } catch {
          // Malformed event — ignore. The next state_changed event will
          // re-publish the same asset_id so we'll catch up.
        }
      });

      // Phase 6 D-137 + RESEARCH Finding #3 — new event-typed listener for
      // photo lifecycle. Plan 01 Task 2 backwards-compat shim preserves the
      // Phase 4 'state_changed' wire contract above; this is a strictly
      // additive new event type. Payload is IDs-only (CP4-B closure):
      //   { inspection_id, asset_id, photo_id, client_photo_id, uploaded_at }
      // We only read asset_id + inspection_id (typeof number guards reject
      // anything else). On a valid event, invalidate two keys via Promise.all
      // so the per-asset inspection list refreshes (drives photo_count badge
      // updates) AND the per-inspection PhotoStrip refetches (placeholder →
      // thumbnail flip; HIST-5.7).
      // Phase 6 UAT-1 fix — cleaning_settings_changed invalidates the
      // GeoJSON cache so cadence/warning changes recolor every pin without
      // a manual refresh. Payload carries only the new {cadence_months,
      // warning_days, updated_at} so the event itself can never carry a
      // credential-adjacent field (CP4 by construction). Body is otherwise
      // ignored — we just need the signal that the singleton row changed.
      es.addEventListener("cleaning_settings_changed", () => {
        setLastEventAt(new Date());
        qc.invalidateQueries({ queryKey: ASSETS_GEOJSON_KEY });
      });

      es.addEventListener("inspection.photo_uploaded", (ev) => {
        try {
          const payload = JSON.parse((ev as MessageEvent).data);
          if (
            typeof payload.asset_id === "number" &&
            typeof payload.inspection_id === "number"
          ) {
            setLastEventAt(new Date());
            void Promise.all([
              qc.invalidateQueries({
                queryKey: ASSET_INSPECTIONS_KEY(payload.asset_id),
              }),
              qc.invalidateQueries({
                queryKey: INSPECTION_PHOTOS_KEY(payload.inspection_id),
              }),
            ]);
          }
        } catch {
          // Malformed event — ignore.
        }
      });

      es.onerror = () => {
        setConnected(false);
        es.close();
        if (!cancelled) {
          reconnectTimer = setTimeout(connect, backoffMs);
          backoffMs = Math.min(backoffMs * 2, MAX_BACKOFF_MS);
        }
      };
    }

    connect();
    return () => {
      cancelled = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      esRef.current?.close();
    };
  }, [qc]);

  return { connected, lastEventAt };
}
